/**
 * Labstep
 *
 * @module grid/NewIndex/coldefs/metadata/numeric
 * @desc Column Definition for metadata numeric
 */

import type {
  EditableCallbackParams,
  ValueGetterParams,
  ValueSetterParams,
} from 'ag-grid-community';
import { ICONS } from 'labstep-web/constants/icons';
import { suppressKeyboardEventClearCells } from 'labstep-web/core/DataGrid/CellEditor/utils';
import { DataGridPlaceholder } from 'labstep-web/core/DataGrid/Placeholder';
import type { CellRendererParams } from 'labstep-web/core/DataGrid/types';
import {
  createOrUpdateValue,
  setValue,
} from 'labstep-web/core/DataGrid/utils';
import { getMetadata } from 'labstep-web/grid/NewIndex/coldefs';
import type { NewIndexColDef } from 'labstep-web/grid/NewIndex/coldefs/types';
import { GridNewIndexColumnHeader } from 'labstep-web/grid/NewIndex/components/ColumnHeader';
import { GridNewIndexService } from 'labstep-web/grid/NewIndex/services/grid-new-index.service';
import { PostFilterFieldTypes } from 'labstep-web/grid/services/grid-filter.service';
import type { Entity } from 'labstep-web/models/entity.model';
import { Metadata } from 'labstep-web/models/metadata';
import type { MetadataType } from 'labstep-web/models/metadata/types';
import { MetadataThread } from 'labstep-web/models/metadata-thread.model';
import rules from 'labstep-web/services/validation/rules';

export const getMetadataNumericColDef = (
  parentEntityClass: string,
  type: MetadataType,
  metadataLabel: string,
  entityName?: string,
): NewIndexColDef => {
  return {
    colId: `metadata:${parentEntityClass}:${type}:${metadataLabel}`,
    sortable: true,
    headerName: metadataLabel,
    headerComponent: GridNewIndexColumnHeader,
    headerComponentParams: {
      icon: ICONS.metadata.secondary,
      cornerIcon:
        ICONS[parentEntityClass as keyof typeof ICONS].primary,
      postFilterSettings: {
        path:
          parentEntityClass !== entityName
            ? `${parentEntityClass}.metadatas`
            : 'metadatas',
        field: 'number',
        entityName: Metadata.entityName,
        fieldType: PostFilterFieldTypes.metadata,
      },
    },
    editable: (params: EditableCallbackParams) => {
      const entity = params.node.data;
      if (
        parentEntityClass === 'purchase_order' &&
        !entity.purchase_order
      ) {
        return false;
      }
      return GridNewIndexService.getEditable(params);
    },
    valueGetter: (params: ValueGetterParams) => {
      if (!params.node) {
        return null;
      }

      const metadata = getMetadata(
        parentEntityClass,
        params.node.data,
        metadataLabel,
        type,
      );

      if (!metadata) {
        return null;
      }

      return metadata.printValue;
    },
    valueSetter: (params: ValueSetterParams) => {
      if (!params.node) {
        return false;
      }

      const newValue = params.newValue;

      let amount = null;
      let unit = null;

      if (typeof newValue === 'string') {
        const regex = /(\d+(?:[.,]\d+)?)(\s*[a-zA-Zµ°±²³´¹ΩΔ\/]*)/;
        const match = newValue.replace(',', '.').match(regex);
        if (!match) {
          return false;
        }
        amount = match[1];
        unit = match[2].trim();
      } else if (typeof newValue === 'number') {
        amount = newValue.toString();
      }

      const metadata = getMetadata(
        parentEntityClass,
        params.node.data,
        metadataLabel,
        type,
      );

      const metadataThreadId =
        parentEntityClass !== params.node.data.entityName
          ? params.node.data[parentEntityClass].metadata_thread.idAttr
          : params.node.data.metadata_thread.idAttr;

      if (!metadata) {
        if (
          parentEntityClass !== params.node.data.entityName &&
          !params.node.data[parentEntityClass]
        ) {
          return false;
        }
        const props = {
          parentName: MetadataThread.entityName,
          parentId: metadataThreadId,
        };
        createOrUpdateValue({
          entityName: Metadata.entityName,
          body: {
            type,
            label: metadataLabel,
            metadata_thread_id: metadataThreadId,
            number: amount,
            unit,
          },
          createProps: props,
        });
        return true;
      }

      if (amount == null) {
        setValue(
          'number',
          null,
          {
            entityName: metadata.entityName,
            id: metadata.idAttr,
          },
          rules.metadata.number,
        );
        return true;
      }

      setValue(
        'number',
        amount,
        {
          entityName: metadata.entityName,
          id: metadata.idAttr,
        },
        rules.metadata.number,
      );
      if (unit && unit.length > 0) {
        setValue(
          'unit',
          unit,
          {
            entityName: metadata.entityName,
            id: metadata.idAttr,
          },
          rules.metadata.unit,
        );
      }
      return true;
    },
    cellRenderer: (params: CellRendererParams<Entity>) => {
      if (!params.node) {
        return null;
      }

      const entity = params.node.data;
      if (
        parentEntityClass === 'purchase_order' &&
        !entity.purchase_order
      ) {
        return null;
      }

      const metadata = getMetadata(
        parentEntityClass,
        params.node.data,
        metadataLabel,
        type,
      );

      if (metadata?.number !== undefined) {
        return `${metadata.number} ${metadata.unitDisplayValue || ''}`;
      }

      return (
        <DataGridPlaceholder
          params={params}
          editable={GridNewIndexService.getEditable(params)}
          placeholder="Enter"
        />
      );
    },
    suppressKeyboardEvent: suppressKeyboardEventClearCells,
    valueClearer: () => null,
  };
};
